const DEEPSEEK_API_KEY = 'sk-ab3e54aa566a46568b8b62cad5ade793';
const DEEPSEEK_API_URL = 'https://api.deepseek.com/v1/chat/completions';

// 生成竞价方案的提示词模板
const generateBiddingPrompt = (taskDescription, taskTTL) => {
  return `请根据以下任务描述生成4个不同的竞价方案，每个方案包含服务商名称、成功率、响应时间、价格和服务细节。

任务描述：${taskDescription}
任务有效时间：${taskTTL}

请直接返回JSON格式，不要包含任何Markdown代码块标记（如\`\`\`json）：

{
  "taskInfo": {
    "title": "任务标题",
    "description": "任务描述",
    "requirements": "任务要求",
    "publishTime": "发布时间",
    "deadline": "截止时间",
    "totalBids": 4
  },
  "biddingData": [
    {
      "id": 1,
      "providerName": "服务商名称",
      "successRate": 85,
      "responseTime": "30分钟前",
      "price": 1500,
      "services": [
        "服务项目1",
        "服务项目2",
        "服务项目3"
      ]
    }
  ],
  "adjustableServices": [
    {
      "id": 1,
      "name": "可选服务名称",
      "description": "服务描述",
      "price": 500,
      "included": true
    }
  ]
}

请确保：
1. 生成4个不同的竞价方案
2. 价格范围合理，根据任务复杂度调整
3. 成功率在70-95%之间
4. 响应时间在5分钟到5小时之间
5. 每个方案包含3-5个具体服务项目
6. 生成3-6个可调整的服务项目
7. 直接返回纯JSON格式，不要包含任何其他标记或说明文字`;
};

// 调用DeepSeek API生成竞价方案
export const generateBiddingPlan = async (taskDescription, taskTTL) => {
  const startTime = Date.now();
  
  try {
    console.log('🚀 开始调用DeepSeek API...');
    
    const response = await fetch(DEEPSEEK_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${DEEPSEEK_API_KEY}`
      },
      body: JSON.stringify({
        model: 'deepseek-chat',
        messages: [
          {
            role: 'user',
            content: generateBiddingPrompt(taskDescription, taskTTL)
          }
        ],
        temperature: 0.7,
        max_tokens: 2000
      })
    });

    if (!response.ok) {
      throw new Error(`API调用失败: ${response.status}`);
    }

    const data = await response.json();
    
    if (data.choices && data.choices[0] && data.choices[0].message) {
      const content = data.choices[0].message.content;
      console.log('📥 收到API响应:', content.substring(0, 200) + '...');
      
      // 尝试解析JSON响应
      try {
        // 处理可能包含Markdown代码块的情况
        let jsonContent = content.trim();
        
        // 如果内容被```json和```包围，提取其中的JSON
        if (jsonContent.startsWith('```json')) {
          jsonContent = jsonContent.replace(/^```json\s*/, '').replace(/\s*```$/, '');
        } else if (jsonContent.startsWith('```')) {
          jsonContent = jsonContent.replace(/^```\s*/, '').replace(/\s*```$/, '');
        }
        
        const parsedData = JSON.parse(jsonContent);
        const endTime = Date.now();
        const duration = endTime - startTime;
        
        console.log(`✅ DeepSeek API调用成功！耗时: ${duration}ms`);
        console.log('📊 解析后的数据:', parsedData);
        
        return parsedData;
      } catch (parseError) {
        console.error('❌ JSON解析失败:', parseError);
        console.error('🔍 原始内容:', content);
        
        const endTime = Date.now();
        const duration = endTime - startTime;
        console.log(`⏱️ API调用耗时: ${duration}ms，但解析失败，使用默认数据`);
        
        // 如果解析失败，返回默认数据
        return generateDefaultBiddingData(taskDescription);
      }
    } else {
      throw new Error('API响应格式错误');
    }
  } catch (error) {
    const endTime = Date.now();
    const duration = endTime - startTime;
    
    console.error('❌ DeepSeek API调用失败:', error);
    console.log(`⏱️ API调用耗时: ${duration}ms，使用默认数据`);
    
    // 返回默认数据
    return generateDefaultBiddingData(taskDescription);
  }
};

// 生成默认的竞价数据（当API调用失败时使用）
const generateDefaultBiddingData = (taskDescription) => {
  return {
    taskInfo: {
      title: '自定义任务',
      description: taskDescription,
      requirements: '根据任务描述定制化服务',
      publishTime: new Date().toLocaleString(),
      deadline: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toLocaleString(),
      totalBids: 4
    },
    biddingData: [
      {
        id: 1,
        providerName: '智能服务商A',
        successRate: 88,
        responseTime: '30分钟前',
        price: 1500,
        services: [
          '专业需求分析',
          '定制化解决方案',
          '全程技术支持',
          '质量保证服务'
        ]
      },
      {
        id: 2,
        providerName: '优质服务商B',
        successRate: 92,
        responseTime: '1小时前',
        price: 2000,
        services: [
          '深度需求调研',
          '高级定制开发',
          '专业团队服务',
          '售后保障支持'
        ]
      },
      {
        id: 3,
        providerName: '经济服务商C',
        successRate: 75,
        responseTime: '3小时前',
        price: 800,
        services: [
          '基础服务提供',
          '标准解决方案',
          '基础技术支持'
        ]
      },
      {
        id: 4,
        providerName: '高端服务商D',
        successRate: 95,
        responseTime: '15分钟前',
        price: 3000,
        services: [
          'VIP专属服务',
          '顶级专家团队',
          '全方位定制方案',
          '终身技术支持',
          '优先响应服务'
        ]
      }
    ],
    adjustableServices: [
      {
        id: 1,
        name: '高级定制服务',
        description: '提供更高级别的定制化服务',
        price: 800,
        included: true
      },
      {
        id: 2,
        name: '快速响应服务',
        description: '提供更快的响应时间',
        price: 500,
        included: true
      },
      {
        id: 3,
        name: '质量保证服务',
        description: '额外的质量保证和测试服务',
        price: 600,
        included: false
      },
      {
        id: 4,
        name: '售后支持服务',
        description: '延长售后支持时间',
        price: 400,
        included: false
      }
    ]
  };
};